var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useCallback, useEffect, useMemo } from 'react';
/**
 * Custom hook for fetching data with proper caching and dependency tracking
 * @param fetchFunction The function to fetch data
 * @param dependencies Array of dependencies that trigger a refetch when changed
 * @param functionName Optional name for debugging purposes
 */
export var useFetchData = function (fetchFunction, dependencies, functionName) {
    if (dependencies === void 0) { dependencies = []; }
    if (functionName === void 0) { functionName = ''; }
    var _a = useState({
        data: [],
        isLoading: false,
        error: null
    }), state = _a[0], setState = _a[1];
    // Memoize the fetch function to prevent it from changing on every render
    var memoizedFetchFunction = useCallback(fetchFunction, []);
    // Memoize the fetch operation
    var fetch = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        return __awaiter(void 0, void 0, void 0, function () {
            var response_1, dataArray_1, error_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!args.every(function (arg) { return !!arg; }) && args.length > 0) {
                            return [2 /*return*/];
                        }
                        setState(function (prev) { return (__assign(__assign({}, prev), { isLoading: true, error: null })); });
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, memoizedFetchFunction.apply(void 0, args)];
                    case 2:
                        response_1 = _a.sent();
                        if (!response_1.success) {
                            setState(function (prev) { return (__assign(__assign({}, prev), { isLoading: false, error: response_1.error || 'Failed to fetch data', data: [] })); });
                            return [2 /*return*/];
                        }
                        if (!response_1.data) {
                            setState(function (prev) { return (__assign(__assign({}, prev), { isLoading: false, error: 'No data received', data: [] })); });
                            return [2 /*return*/];
                        }
                        dataArray_1 = Array.isArray(response_1.data) ? response_1.data : [response_1.data];
                        setState(function (prev) { return (__assign(__assign({}, prev), { data: dataArray_1, isLoading: false, error: null })); });
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        setState(function (prev) { return (__assign(__assign({}, prev), { isLoading: false, error: error_1 instanceof Error ? error_1.message : 'Error fetching data', data: [] })); });
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    }, [memoizedFetchFunction]); // Only depend on the memoized fetch function
    // Memoize dependencies to prevent unnecessary effect triggers
    var memoizedDependencies = useMemo(function () { return dependencies; }, dependencies);
    useEffect(function () {
        var mounted = true;
        var executeFetch = function () { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!mounted)
                            return [2 /*return*/];
                        setState(function (prev) { return (__assign(__assign({}, prev), { data: [], error: null })); });
                        if (!(memoizedDependencies.length === 0 || memoizedDependencies.every(function (dep) { return !!dep; }))) return [3 /*break*/, 2];
                        return [4 /*yield*/, fetch.apply(void 0, memoizedDependencies)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); };
        executeFetch();
        // Cleanup function to prevent setting state on unmounted component
        return function () {
            mounted = false;
        };
    }, [fetch, memoizedDependencies]); // Only depend on memoized values
    return state;
};
